import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Briefcase, User, Landmark, Plus, CheckCircle2, Trash2, HandCoins } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COLORS = ["#F2B705", "#22c55e", "#38bdf8", "#f472b6", "#a78bfa", "#fb923c", "#f87171", "#34d399", "#e879f9", "#facc15", "#94a3b8", "#4ade80"];
const STATUS_CLS = { paid: "bg-green-500/15 text-green-400", due_soon: "bg-amber-400/15 text-amber-300", due: "bg-white/10 text-white/60", overdue: "bg-red-500/15 text-red-400" };

const Stat = ({ label, value, cls = "text-white", testid }) => (
  <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4" data-testid={testid}>
    <div className="text-white/45 text-[11px] uppercase tracking-wider">{label}</div>
    <div className={`font-display font-black text-xl mt-1 ${cls}`}>{money(value)}</div>
  </div>
);

export default function ExpenseDashboard({ token }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("business");
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [form, setForm] = useState(null); // {kind: 'expense'|'bill'|'draw'}
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    const q = preset === "custom" ? `preset=custom&start=${custom.start}&end=${custom.end}` : `preset=${preset}`;
    axios.get(`${API}/expense-dashboard?${q}`, auth).then((r) => setData(r.data)).catch(() => toast.error("Could not load expense dashboard"));
    // eslint-disable-next-line
  }, [preset, custom.start, custom.end, token]);
  useEffect(() => { load(); }, [load]);

  const post = async (url, body, msg) => {
    try { await axios.post(`${API}${url}`, body, auth); toast.success(msg); setForm(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const b = data.business, p = data.personal, o = data.owner_overview;
  const cats = view === "personal" ? p.by_category : b.by_category;
  const catList = data.categories[view === "personal" ? "personal" : "business"];

  return (
    <div className="space-y-5" data-testid="expense-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-[#0C0C0C] border border-white/10 rounded-lg p-1">
          {[["business", "Business", Briefcase], ["personal", "Personal", User], ["overview", "Owner Overview", Landmark]].map(([k, l, I]) => (
            <button key={k} data-testid={`money-view-${k}`} onClick={() => setView(k)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md ${view === k ? "bg-[#F2B705] text-black" : "text-white/60 hover:text-white"}`}>
              <I size={13} /> {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[["month", "This Month"], ["last_month", "Last Month"], ["ytd", "Year to Date"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`money-preset-${k}`} onClick={() => setPreset(k)}
              className={`text-xs px-3 py-1.5 rounded border ${preset === k ? "border-[#F2B705] text-[#F2B705]" : "border-white/15 text-white/55 hover:text-white"}`}>{l}</button>
          ))}
          {preset === "custom" && (
            <span className="flex gap-1">
              <input type="date" data-testid="money-custom-start" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className="bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-xs text-white" />
              <input type="date" data-testid="money-custom-end" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className="bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-xs text-white" />
            </span>
          )}
        </div>
      </div>

      {view === "business" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat testid="biz-revenue" label="Revenue" value={b.revenue} cls="text-green-400" />
          <Stat testid="biz-expenses" label="Business Expenses" value={b.expenses} cls="text-red-400" />
          <Stat testid="biz-net" label="Net Operating Profit" value={b.net} cls={b.net >= 0 ? "text-[#F2B705]" : "text-red-400"} />
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4" data-testid="biz-pct">
            <div className="text-white/45 text-[11px] uppercase tracking-wider">Expenses % of Revenue</div>
            <div className="font-display font-black text-xl mt-1 text-white">{b.expense_pct_of_revenue}%</div>
          </div>
        </div>
      )}
      {view === "personal" && (
        <div className="grid grid-cols-3 gap-3">
          <Stat testid="per-income" label="Owner Income (draws)" value={p.owner_income} cls="text-green-400" />
          <Stat testid="per-bills" label="Personal Bills" value={p.bills} cls="text-red-400" />
          <Stat testid="per-remaining" label="Personal Money Remaining" value={p.remaining} cls={p.remaining >= 0 ? "text-[#F2B705]" : "text-red-400"} />
        </div>
      )}
      {view === "overview" && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat testid="ov-net" label="Business Net" value={o.business_net} cls="text-green-400" />
          <Stat testid="ov-draw" label="Owner Pay / Draw" value={o.owner_draw} cls="text-[#F2B705]" />
          <Stat testid="ov-cash" label="Business Cash Available" value={o.business_cash_available} />
          <Stat testid="ov-bills" label="Personal Bills" value={o.personal_bills} cls="text-red-400" />
          <Stat testid="ov-remaining" label="Personal Remaining" value={o.personal_remaining} cls={o.personal_remaining >= 0 ? "text-green-400" : "text-red-400"} />
        </div>
      )}

      {view !== "overview" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-white/45 mb-2">Where My Money Went</p>
            {cats.length === 0 ? <p className="text-white/35 text-sm py-8 text-center">No expenses in this period.</p> : (
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={cats} dataKey="amount" nameKey="category" outerRadius={85} label={(e) => e.category}>
                    {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#111", border: "1px solid #333" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-white/45 mb-2">Monthly Trend</p>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.trend}>
                <XAxis dataKey="month" stroke="#555" fontSize={10} /><YAxis stroke="#555" fontSize={10} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#111", border: "1px solid #333" }} /><Legend />
                {view === "business" ? (<>
                  <Bar dataKey="revenue" fill="#22c55e" name="Revenue" />
                  <Bar dataKey="business" fill="#F2B705" name="Business Expenses" />
                </>) : <Bar dataKey="personal" fill="#f472b6" name="Personal Expenses" />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-xs uppercase tracking-widest text-white/45">Recurring Bills {data.next_due ? `· next due ${data.next_due}` : ""}</p>
          <div className="flex gap-2">
            <button data-testid="add-expense-btn" onClick={() => setForm({ kind: "expense", ledger: view === "personal" ? "personal" : "business", category: "Other", amount: "", vendor: "", date: new Date().toISOString().slice(0, 10) })}
              className="text-xs flex items-center gap-1 border border-white/20 text-white/70 px-3 py-1.5 rounded hover:bg-white/10"><Plus size={12} /> One-time Expense</button>
            <button data-testid="add-bill-btn" onClick={() => setForm({ kind: "bill", ledger: view === "personal" ? "personal" : "business", category: "Other", amount: "", label: "", frequency: "monthly", next_due: new Date().toISOString().slice(0, 10) })}
              className="text-xs flex items-center gap-1 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black"><Plus size={12} /> Recurring Bill</button>
            <button data-testid="add-draw-btn" onClick={() => setForm({ kind: "draw", amount: "", note: "" })}
              className="text-xs flex items-center gap-1 bg-[#F2B705] text-black font-bold px-3 py-1.5 rounded hover:bg-white"><HandCoins size={12} /> Owner Pay / Draw</button>
          </div>
        </div>

        {form && (
          <div data-testid="money-form" className="mb-3 rounded-lg border border-[#F2B705]/30 bg-[#0A0A0A] p-3 flex flex-wrap gap-2 items-end">
            {form.kind !== "draw" && (<>
              <select data-testid="form-ledger" value={form.ledger} onChange={(e) => setForm({ ...form, ledger: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-2 text-xs text-white">
                <option value="business">Business</option><option value="personal">Personal</option>
              </select>
              <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-2 text-xs text-white">
                {(data.categories[form.ledger] || catList).map((c) => <option key={c}>{c}</option>)}
              </select>
            </>)}
            <input data-testid="form-amount" type="number" step="0.01" placeholder="Amount $" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-24 bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />
            {form.kind === "expense" && <input data-testid="form-vendor" placeholder="Vendor / what for" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="flex-1 min-w-[120px] bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />}
            {form.kind === "bill" && (<>
              <input data-testid="form-label" placeholder="Bill name (e.g. Shop Rent)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="flex-1 min-w-[120px] bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />
              <select data-testid="form-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-2 text-xs text-white">
                {["weekly", "monthly", "quarterly", "yearly"].map((f) => <option key={f}>{f}</option>)}
              </select>
              <input data-testid="form-next-due" type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />
            </>)}
            {form.kind === "draw" && <input data-testid="form-note" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="flex-1 min-w-[120px] bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />}
            {form.kind === "expense" && <input data-testid="form-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-2 text-xs text-white" />}
            <button data-testid="form-save" disabled={saving} onClick={async () => {
              const amt = parseFloat(form.amount);
              if (!amt || amt <= 0) { toast.error("Enter an amount greater than $0."); return; }
              setSaving(true);
              try {
                if (form.kind === "draw") await post("/owner-draws", { amount: amt, note: form.note }, "Owner draw recorded — moved to your personal side.");
                else if (form.kind === "bill") await post("/recurring-bills", { ...form, amount: amt }, "Recurring bill saved — it will post itself every period.");
                else await post("/expenses", { ...form, amount: amt, paid: true }, "Expense recorded.");
              } finally { setSaving(false); }
            }} className="text-xs font-bold bg-[#F2B705] text-black px-4 py-2 rounded hover:bg-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
            <button onClick={() => setForm(null)} className="text-xs text-white/50 px-2 py-2">Cancel</button>
          </div>
        )}

        {data.bills.length === 0 ? <p className="text-white/35 text-sm">No bills in this period.</p> : (
          <div className="space-y-1.5">
            {data.bills.map((bl) => (
              <div key={bl.expense_id} data-testid={`bill-row-${bl.expense_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/5 bg-[#0A0A0A] px-3 py-2 text-sm">
                <span className="text-white/80">{bl.label} <span className="text-white/35 text-xs">· {bl.ledger} · {bl.category} · due {bl.due_date}{bl.due_date > data.range.end ? " · upcoming (outside this period)" : ""}</span></span>
                <span className="flex items-center gap-2">
                  <span className="font-bold text-white">{money(bl.amount)}</span>
                  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${STATUS_CLS[bl.status]}`}>{bl.status.replace("_", " ")}</span>
                  {bl.status !== "paid" && (
                    <button data-testid={`bill-paid-${bl.expense_id}`} onClick={() => post(`/expenses/${bl.expense_id}/mark-paid`, {}, "Marked paid.")}
                      className="text-[10px] flex items-center gap-1 border border-green-500/40 text-green-400 px-2 py-1 rounded hover:bg-green-500 hover:text-black"><CheckCircle2 size={10} /> Mark Paid</button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {data.recurring_bills.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/5">
            <p className="text-[11px] uppercase tracking-widest text-white/35 mb-2">Recurring templates</p>
            <div className="flex flex-wrap gap-2">
              {data.recurring_bills.map((rb) => (
                <span key={rb.id} className="flex items-center gap-2 text-xs border border-white/10 rounded-full px-3 py-1.5 text-white/60">
                  {rb.label || rb.category} · {money(rb.amount)}/{rb.frequency} · {rb.ledger}
                  <button data-testid={`rb-delete-${rb.id}`} title="Stop this recurring bill" onClick={async () => {
                    if (!window.confirm(`Stop the recurring "${rb.label}" bill? Unpaid auto-posted copies are removed; paid history is kept.`)) return;
                    try { await axios.delete(`${API}/recurring-bills/${rb.id}`, auth); toast.success("Recurring bill stopped."); load(); } catch { toast.error("Could not delete"); }
                  }} className="text-white/30 hover:text-red-400"><Trash2 size={11} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
